import { Building, Dealer, OrderStatus } from '../types';

const statusColorMap: Record<string, string> = {
  '0': '#757575', // Unknown - Grey
  '1': '#757575', // Draft - Grey
  '2': '#757575', // Checked Out - Grey
  '3': '#757575', // In Queue - Grey
  '4': '#757575', // Saw - Grey
  '5': '#757575', // Weld - Grey
  '6': '#757575', // Paint - Grey
  '7': '#F44336', // On Hold - Red
  '8': '#4CAF50', // Complete - Green
  '9': '#4CAF50', // Ready For Pickup - Green
  '10': '#4CAF50', // Ready For Shipment - Green
  '11': '#4CAF50', // Shipped - Green
};

/**
 * Converts order status from string (C# enum name) or number to OrderStatus enum number
 * Handles both string values like "CheckedOut", "InQueue" and numeric values
 * @param status - Status as string (C# enum name) or number
 * @returns OrderStatus enum number
 */
export const normalizeOrderStatus = (status: string | number | OrderStatus): OrderStatus => {
  // If already a number, return it
  if (typeof status === 'number') {
    return status as OrderStatus;
  }

  // If it's a string, map C# enum names to TypeScript enum values
  if (typeof status === 'string') {
    const statusMap: Record<string, OrderStatus> = {
      Draft: OrderStatus.DRAFT,
      CheckedOut: OrderStatus.CHECKED_OUT,
      InQueue: OrderStatus.IN_QUEUE,
      Saw: OrderStatus.SAW,
      Weld: OrderStatus.WELD,
      Paint: OrderStatus.PAINT,
      OnHold: OrderStatus.ON_HOLD,
      Complete: OrderStatus.COMPLETE,
      ReadyForPickup: OrderStatus.READY_FOR_PICKUP,
      ReadyForShipment: OrderStatus.READY_FOR_SHIPMENT,
      Shipped: OrderStatus.SHIPPED,
    };

    const mapped = statusMap[status];
    if (mapped !== undefined) {
      return mapped;
    }

    // Try parsing as number string
    const numValue = Number(status);
    if (!isNaN(numValue)) {
      return numValue as OrderStatus;
    }

    // Log warning for invalid status string
    console.warn(`Invalid order status string: "${status}". Defaulting to DRAFT.`);
  }

  // Default to DRAFT if unable to parse
  console.warn(
    `Unable to normalize order status. Value: ${status}, Type: ${typeof status}. Defaulting to DRAFT.`
  );
  return OrderStatus.DRAFT;
};

export const getOrderStatusLabel = (status: OrderStatus | string | number): string => {
  // Normalize status first to handle string values
  const normalizedStatus = normalizeOrderStatus(status);

  switch (normalizedStatus) {
    case OrderStatus.DRAFT:
      return 'Draft';
    case OrderStatus.CHECKED_OUT:
      return 'Checked Out';
    case OrderStatus.IN_QUEUE:
      return 'In Queue';
    case OrderStatus.SAW:
      return 'Saw';
    case OrderStatus.WELD:
      return 'Weld';
    case OrderStatus.PAINT:
      return 'Paint';
    case OrderStatus.ON_HOLD:
      return 'On Hold';
    case OrderStatus.COMPLETE:
      return 'Completed';
    case OrderStatus.READY_FOR_PICKUP:
      return 'Ready For Pickup';
    case OrderStatus.READY_FOR_SHIPMENT:
      return 'Ready For Shipment';
    case OrderStatus.SHIPPED:
      return 'Shipped';
    default:
      return 'Unknown';
  }
};

/**
 * Gets a color for the order status (legacy function using old statusColorMap)
 * @param status - The order status
 * @returns A color string for the status
 */
export const getOrderStatusColor = (status: number | string | undefined): string => {
  if (status === undefined) return 'grey';

  return statusColorMap[status.toString()] || 'grey';
};

/**
 * Gets the background color for a dashboard order based on its color code
 * @param colorCode - The color code ('red', 'orange', or 'neutral')
 * @returns A hex color string
 */
export const getDashboardOrderColor = (colorCode: string): string => {
  switch (colorCode) {
    case 'red':
      return '#F44336'; // Red for past due
    case 'orange':
      return '#FF9800'; // Orange for due within 7 days
    case 'neutral':
    default:
      return '#757575'; // Gray for neutral
  }
};

/**
 * Rounds a monetary value to the nearest cent to avoid IEEE 754 floating-point
 * drift when summing prices (e.g. 84.83 + 120.66 + 84.83 drifting to 290.31
 * instead of 290.32).
 */
export const roundToCents = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Sums an array of monetary values with per-item cent rounding to prevent
 * floating-point accumulation errors.
 */
export const sumPrices = (values: number[]): number =>
  roundToCents(values.reduce((sum, v) => sum + roundToCents(v), 0));

/**
 * Formats the price with currency symbol
 * @param price - The price to format
 * @returns Formatted price string with currency symbol
 */
export const formatPrice = (price: number | undefined): string => {
  if (price === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price);
};

/**
 * Calculates the total number of trusses across all buildings in an order
 * @param buildings - Array of buildings in the order
 * @returns Total truss count
 */
export const calculateTotalTrusses = (buildings: Building[] | undefined): number => {
  if (!buildings || buildings.length === 0) return 0;
  return buildings.reduce((total, building) => total + (building.trusses?.length ?? 0), 0);
};

/**
 * Determines if a building state differs from the comparison state and needs validation
 * @param buildingState - The selected building state
 * @param addressStateToCompare - The state to compare against (shipping address or dealer state)
 * @param confirmedState - The previously confirmed state (to avoid validation for already confirmed states)
 * @returns True if building state differs and needs validation, false otherwise
 */
export const needsBuildingStateValidation = (
  buildingState: string | undefined | null,
  addressStateToCompare: string | undefined | null,
  confirmedState: string | null
): boolean => {
  // Return true if:
  // 1. There's a state to compare against
  // 2. Building state is different from the comparison state
  // 3. Building state is not empty/null
  // 4. This state change hasn't been confirmed yet
  return !!(
    addressStateToCompare &&
    buildingState &&
    buildingState !== addressStateToCompare &&
    confirmedState !== buildingState
  );
};

/**
 * Gets the state to compare against for building state validation
 * @param shippingAddressState - State from shipping address
 * @param selectedDealer - The selected dealer object
 * @returns The state to use for comparison
 */
export const getComparisonState = (
  shippingAddressState: string | undefined | null,
  selectedDealer: Dealer | undefined
): string | undefined => {
  return shippingAddressState || selectedDealer?.state;
};
